import React from 'react';
import { PurchaseOrderForm } from './PurchaseOrderForm';
import { createPurchaseOrder } from '../store';
import { usePurchaseOrderFormOptions } from '../usePurchaseOrderFormOptions';
import type { LineItem } from '@/lib/line-items';
import type { NewPurchaseOrderInput, PurchaseOrder } from '../types';

interface PurchaseOrderCreateDialogProps {
  isOpen: boolean;
  onClose: () => void;
  /** Called with the saved purchase order (the dialog stays the caller's to close). */
  onCreated: (purchaseOrder: PurchaseOrder) => void;
  /** Prefill. */
  defaults?: { title?: string; projectId?: string; description?: string; vendorId?: string; lines?: LineItem[]; taxPct?: string; otherAmount?: string };
  /** Record the new PO as created FROM this warranty (verified by the server). */
  warrantyId?: string;
  /** Record the new PO as created FROM this change order (verified by the server; a change order has at most one). */
  changeOrderId?: string;
  /** Record the new PO as created FROM this selection (verified by the server; a selection has at most one). */
  selectionId?: string;
  /** Record the new PO as created FROM this material order (verified by the server; a material order has at most one standing PO). */
  materialListId?: string;
}

/**
 * "Create a purchase order from somewhere else": THE purchase order form
 * (the same one the Purchase Orders page uses), with its options loaded and
 * saving wired through the same store -- the caller only supplies a prefill
 * and, optionally, the Warranty or Change Order it comes from. Nothing PO-specific is
 * re-implemented by the caller.
 */
export function PurchaseOrderCreateDialog({ isOpen, onClose, onCreated, defaults, warrantyId, changeOrderId, selectionId, materialListId }: PurchaseOrderCreateDialogProps) {
  const { projects, users, vendors, currentAuthor } = usePurchaseOrderFormOptions();

  const handleSave = async (data: NewPurchaseOrderInput) => {
    const created = await createPurchaseOrder({ ...data, ...(warrantyId ? { warrantyId } : {}), ...(changeOrderId ? { changeOrderId } : {}), ...(selectionId ? { selectionId } : {}), ...(materialListId ? { materialListId } : {}) }, currentAuthor);
    if (created) onCreated(created);
    return created;
  };

  return (
    <PurchaseOrderForm
      purchaseOrder={null}
      projects={projects}
      users={users}
      vendors={vendors}
      onClose={onClose}
      onSave={handleSave}
      isOpen={isOpen}
      defaults={defaults}
    />
  );
}
